import { forwardRef, useCallback, useEffect, useMemo, useRef } from 'react';
import { RevoGrid } from '@revolist/react-datagrid';
import type { ColumnProp } from '@revolist/react-datagrid';
import { Card, useTheme } from '@mui/material';

import { FIELD_HEIGHT, setEditorTheme } from './SelectCellEditor';
import { REVO_GRID_HEADER_SIZE, REVO_GRID_ROW_SIZE, revoGridTableSx } from './revoGridTableSx';
import { RevoGridTableExtras, RevoGridTableProps } from './types';

const DEFAULT_MIN_VISIBLE_ROWS = 4;
const DEFAULT_MAX_VISIBLE_ROWS = 10;

export const RevoGridTable = forwardRef<HTMLRevoGridElement, RevoGridTableProps>(
  (
    {
      columns,
      source,
      rowSize = REVO_GRID_ROW_SIZE,
      height,
      minVisibleRows = DEFAULT_MIN_VISIBLE_ROWS,
      maxVisibleRows = DEFAULT_MAX_VISIBLE_ROWS,
      additionalData,
      sx,
      ...gridProps
    },
    ref
  ) => {
    const theme = useTheme();
    /**
     * Kept alongside the forwarded ref because `onOpenEditor` below needs the
     * grid API, and the call site may not pass a ref at all.
     */
    const gridRef = useRef<HTMLRevoGridElement | null>(null);

    const setRefs = useCallback(
      (node: HTMLRevoGridElement | null) => {
        gridRef.current = node;
        if (typeof ref === 'function') {
          ref(node);
        } else if (ref) {
          ref.current = node;
        }
      },
      [ref]
    );

    /**
     * Cell editors render in their own React root and cannot read context, so
     * hand them the live theme. Safe in an effect: editors only mount on user
     * interaction, long after this has run.
     */
    useEffect(() => {
      setEditorTheme(theme);
    }, [theme]);

    const onOpenEditor = useCallback((rowIndex: number, prop: ColumnProp) => {
      void gridRef.current?.setCellEdit(rowIndex, prop);
    }, []);

    /**
     * Templates are hyperscript and cannot read the theme or the grid, so both
     * arrive here — merged under whatever the call site passed, which wins.
     */
    const gridExtras = useMemo<RevoGridTableExtras>(
      () => ({
        field: {
          border: theme.palette.divider,
          radius: `${theme.shape.borderRadius}px`,
          bg: theme.palette.background.default,
          text: theme.palette.text.primary,
          icon: theme.palette.action.active,
          height: `${FIELD_HEIGHT}px`,
          fontSize: `${theme.typography.body2.fontSize}`,
        },
        onOpenEditor,
        ...additionalData,
      }),
      [theme, onOpenEditor, additionalData]
    );

    // `revo-grid` is `height: 100%` with a 300px min-height, so the wrapper has
    // to own the height — otherwise a 2-row grid is as tall as a 20-row one.
    const visibleRows = Math.min(Math.max(source?.length, minVisibleRows), maxVisibleRows);

    return (
      <Card
        sx={[
          revoGridTableSx(theme),
          {
            height: height ?? REVO_GRID_HEADER_SIZE + rowSize * visibleRows,
            backgroundColor: 'background.default',
            overflow: 'hidden',
          },
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
      >
        <RevoGrid
          ref={setRefs}
          theme={theme.palette.mode === 'dark' ? 'darkMaterial' : 'material'}
          columns={columns}
          source={source}
          rowSize={rowSize}
          additionalData={gridExtras}
          {...gridProps}
        />
      </Card>
    );
  }
);
